import React, {useEffect} from 'react'
import {useSelector} from 'react-redux'
import {mapEventChannel} from 'events/map'
import {useInAppFirst} from 'hooks/useInAppFirst'
import {getLanguageCode} from 'modules/meta/selectors'
import {getHotelIds} from 'modules/sapiSearch/selectors'
import {getShowHowToCancelLink} from 'modules/searchApi/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Footer as FooterAtlas} from '@vio/atlas/Footer'
import {FooterLink} from '@vio/atlas/Footer/FooterLinkButton'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {LinkInterceptor} from '@vio/core/src/_web/utils/embedded/components/LinkInterceptor'
import {getLocalizedFooterUrl} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useGetBusinessUnitQuery} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {CookiePreferencesLink} from '@vio/shared/src/search/cookies/CookiePreferencesLink'
import {TrustpilotWidget} from '@vio/shared/src/search/TrustpilotWidget'

import {GetTheAppFooterWidget} from './GetTheAppFooterWidget/GetTheAppFooterWidget'

interface Props {
  forcedLayout?: 'compact' | 'wide'
}

export const CompactFooter = ({forcedLayout}: Props) => {
  const {brand} = useBrand()
  const {retention, shell} = useBrandCapabilities()
  const {isMobile} = useDeviceLayout()
  const isEmbedded = useIsEmbeddedWithBrand()
  const {ref, inView} = useInView({threshold: 0.7})
  const hotels = useSelector(getHotelIds)
  const languageCode = useSelector(getLanguageCode) || 'en'
  const showHowToCancel = useSelector(getShowHowToCancelLink)
  const {isInAppFirst} = useInAppFirst()
  const {data, isLoading} = useGetBusinessUnitQuery(undefined, {
    skip: !shell.hasTrustpilot
  })

  useEffect(() => {
    if (isMobile && hotels?.length !== 1) {
      // for mobile, emit the footerInView event when its in view
      mapEventChannel.emit('footerInView', inView)
    }
  }, [isMobile, inView])

  // InAppFirst mode (sc-185842) strips all site footer chrome in the partner's
  // in-app browser. Keep this after every hook call to preserve hook order.
  if (isInAppFirst) return null

  return (
    <>
      {isEmbedded && (
        <LinkInterceptor
          containerRef={ref as unknown as React.RefObject<HTMLElement>}
        />
      )}
      <div ref={ref}>
        <FooterAtlas
          brand={brand}
          forcedLayout={forcedLayout}
          widget={
            shell.hasTrustpilot && (
              <TrustpilotWidget
                isLoading={isLoading}
                numberOfReviews={data?.numberOfReviews?.total}
                stars={data?.score?.stars}
                trustScore={data?.score?.trustScore}
              />
            )
          }
          getTheAppSlot={retention.hasGetTheApp && <GetTheAppFooterWidget />}
          CookiePreferencesLinkSlot={<CookiePreferencesLink brand={brand} />}
          howToCancelLinkSlot={
            showHowToCancel &&
            brand.links.howToCancelUrl && (
              <li>
                <FooterLink
                  href={getLocalizedFooterUrl(
                    brand.links.howToCancelUrl,
                    languageCode
                  )}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="HowToCancel"
                    id="howToCancel"
                    defaultMessage="How to cancel"
                  />
                </FooterLink>
              </li>
            )
          }
          languageCode={languageCode}
        />
      </div>
    </>
  )
}
